import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { IconButton, Switch, Tooltip } from '@mui/material'
import Paper from '@/components/Paper'
import {
    DataTableCustomized,
    DataTableColumn,
} from '@/components/ui/data-table-customized'
import {
    FilterBar,
    FilterItemConfig,
} from '@/components/ui/data-table-customized'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import { getCommissionPolicies } from '@/modules/Affiliate/services'
import {
    getCommissionPolicyScopeFilterOptions,
    POLICY_ACTIVE_STATUS_OPTIONS,
} from '@/modules/Affiliate/constants'
import { CommissionPolicy } from '@/modules/Affiliate/types'
import { formatFromUTC } from '@/utils/function'
import EditIcons from '@/icons/EditIcons'
import DeleteOutlineIcons from '@/icons/DeleteOutlineIcons'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

interface CommissionPolicyListProps {
    affiliateId?: number
    onEditPolicy?: (policy: CommissionPolicy) => void
    onDeletePolicy?: (policy: CommissionPolicy) => void
    onTogglePolicy?: (policy: CommissionPolicy) => void
    canUpdatePolicy?: boolean
    canDeletePolicy?: boolean
}

interface PolicyFilters {
    scope: string
    is_active: string
    owner_type: string
}

const CommissionPolicyList: React.FC<CommissionPolicyListProps> = ({
    affiliateId,
    onEditPolicy,
    onDeletePolicy,
    onTogglePolicy,
    canUpdatePolicy = false,
    canDeletePolicy = false,
}) => {
    const { t } = useTranslation('affiliate')

    const {
        params,
        filters,
        queryParams,
        setParam,
        setFilter,
        searchInputProps,
    } = useUrlSearchParams<PolicyFilters>({
        defaults: { page: 1, page_size: 10 },
        filterDefaults: { scope: '', is_active: '', owner_type: '' },
    })

    const { control } = useForm<PolicyFilters>({
        defaultValues: filters,
        values: filters,
    })

    const { data, renderState } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.AFFILIATE_COMMISSION_POLICIES,
            affiliateId,
            queryParams,
            filters,
        ] as const,
        queryFn: () =>
            getCommissionPolicies({
                page: params.page,
                page_size: params.page_size,
                q: queryParams.q,
                scope: filters.scope || undefined,
                is_active: filters.is_active || undefined,
                owner_type: affiliateId
                    ? undefined
                    : (filters.owner_type as 'global' | 'affiliate' | 'all'),
                affiliate_id: affiliateId,
            }),
        placeholderData: true,
    })

    const policies = data?.results || []
    const pagination = data?.pagination
    const stateComponent = renderState()

    const filterItems: FilterItemConfig[] = useMemo(() => {
        const items: FilterItemConfig[] = [
            {
                name: 'scope',
                type: 'select',
                label: t('scope'),
                placeholder: t('scope'),
                options: getCommissionPolicyScopeFilterOptions(t),
            },
            {
                name: 'is_active',
                type: 'select',
                label: t('status'),
                placeholder: t('status'),
                options: POLICY_ACTIVE_STATUS_OPTIONS(t),
            },
        ]

        if (!affiliateId) {
            items.push({
                name: 'owner_type',
                type: 'select',
                label: t('owner'),
                placeholder: t('owner'),
                options: [
                    { label: t('global_policy'), value: 'global' },
                    { label: t('affiliate_policy'), value: 'affiliate' },
                ],
            })
        }

        return items
    }, [t, affiliateId])

    const columns: DataTableColumn<CommissionPolicy>[] = useMemo(() => {
        const baseColumns: DataTableColumn<CommissionPolicy>[] = [
            {
                key: 'policy_name',
                title: t('policy_name'),
                width: 200,
                render: (value) => <span className="font-medium">{value}</span>,
            },
            {
                key: 'owner_type',
                title: t('owner'),
                width: 180,
                render: (_value, row) => {
                    // Global is a declared property. An empty affiliate list means the policy
                    // targets nobody — it must NOT be shown as "global".
                    if (row.is_global) {
                        return t('global_policy')
                    }
                    const affiliateIds = row.affiliate_ids || []
                    if (row.affiliate_names?.length) {
                        return row.affiliate_names.join(', ')
                    }
                    if (row.referral_codes?.length) {
                        return row.referral_codes.join(', ')
                    }
                    if (!affiliateIds.length && !row.affiliate_id) {
                        return (
                            <span className="text-orange-600">
                                {t('policy_no_target')}
                            </span>
                        )
                    }
                    return (
                        row.affiliate_name ||
                        row.referral_code ||
                        `ID: ${row.affiliate_id || affiliateIds.join(', ')}`
                    )
                },
            },
            {
                key: 'scope',
                title: t('scope'),
                width: 130,
                formatter: {
                    type: 'status',
                    colorMap: {
                        default: 'default',
                        product_group: 'info',
                        product: 'success',
                    },
                    labelMap: {
                        default: t('scope_default'),
                        product_group: t('scope_product_group'),
                        product: t('scope_product'),
                    },
                },
            },
            {
                key: 'product_group_name',
                title: t('product_group'),
                width: 150,
                render: (value, row) => {
                    if (
                        row.scope !== 'product_group' ||
                        !row.product_group_id
                    ) {
                        return '-'
                    }
                    return value || `ID: ${row.product_group_id}`
                },
            },
            {
                key: 'product_name',
                title: t('product'),
                width: 200,
                render: (value, row) => {
                    if (row.scope !== 'product' || !row.product_id) {
                        return '-'
                    }
                    return value || `ID: ${row.product_id}`
                },
            },
            {
                key: 'commission_percent',
                title: t('commission_percent'),
                width: 120,
                render: (value) => <span className="font-mono">{value}%</span>,
            },
            {
                key: 'priority',
                title: t('priority'),
                width: 80,
            },
            {
                key: 'effective_date',
                title: t('effective_date'),
                width: 130,
                render: (value) =>
                    value ? formatFromUTC(value, 'DD/MM/YYYY') : '-',
            },
            {
                key: 'expired_date',
                title: t('expired_date'),
                width: 130,
                render: (value) =>
                    value ? formatFromUTC(value, 'DD/MM/YYYY') : '-',
            },
            {
                key: 'is_active',
                title: t('status'),
                width: 140,
                render: (_value, row) => {
                    if (!canUpdatePolicy || !onTogglePolicy) {
                        return row.is_active ? t('active') : t('inactive')
                    }

                    return (
                        <div className="flex items-center gap-2">
                            <Switch
                                size="small"
                                checked={row.is_active}
                                onChange={() => onTogglePolicy(row)}
                            />
                            <span>
                                {row.is_active ? t('active') : t('inactive')}
                            </span>
                        </div>
                    )
                },
            },
        ]

        if (canUpdatePolicy || canDeletePolicy) {
            baseColumns.push({
                key: 'actions',
                title: t('actions'),
                width: 100,
                render: (_value, row) => (
                    <div className="flex gap-1">
                        {canUpdatePolicy && onEditPolicy && (
                            <Tooltip title={t('edit_policy')}>
                                <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => onEditPolicy(row)}
                                >
                                    <EditIcons className="w-4 h-4" />
                                </IconButton>
                            </Tooltip>
                        )}
                        {canDeletePolicy && onDeletePolicy && (
                            <Tooltip title={t('delete_policy')}>
                                <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => onDeletePolicy(row)}
                                >
                                    <DeleteOutlineIcons className="w-4 h-4" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </div>
                ),
            })
        }

        return baseColumns
    }, [
        t,
        canUpdatePolicy,
        canDeletePolicy,
        onEditPolicy,
        onDeletePolicy,
        onTogglePolicy,
    ])

    const handlePageChange = (page: number) => setParam('page', page, false)
    const handlePageSizeChange = (size: number) => {
        setParam('page_size', size)
        setParam('page', 1)
    }

    return (
        <Paper className="space-y-2 my-2">
            <FilterBar
                control={control}
                setFilter={setFilter}
                items={filterItems}
                hideSearch
            />
            {stateComponent || (
                <DataTableCustomized<CommissionPolicy>
                    columns={columns}
                    rows={policies}
                    rowKey={(row) => row.policy_id}
                    size="small"
                    toolbar={{
                        search: {
                            ...searchInputProps,
                            placeholder: t('search_affiliate'),
                        },
                        settings: {
                            show: true,
                            storageKey: 'commission-policies',
                        },
                    }}
                    stickyHeader={{
                        enabled: true,
                        offsetTopSelector: '#main-header',
                    }}
                    pagination={{
                        page: params.page,
                        pageSize: params.page_size,
                        total: pagination?.total || 0,
                        onPageChange: handlePageChange,
                        onPageSizeChange: handlePageSizeChange,
                    }}
                />
            )}
        </Paper>
    )
}

export default CommissionPolicyList
